<template>
  <v-container fluid fill-height>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="4">
        <!-- Информация об инвайте -->
        <v-alert
          v-if="inviteInfo && inviteValidation.valid"
          type="info"
          variant="tonal"
          class="mb-4"
          prominent
        >
          <v-alert-title>
            <v-icon class="mr-2">mdi-email-check</v-icon>
            Приглашение в организацию
          </v-alert-title>
          <div class="mt-2">
            <p><strong>Организация:</strong> {{ inviteValidation.tenant_name }}</p>
            <p><strong>Роль:</strong> {{ getRoleDisplay(inviteValidation.role || '') }}</p>
            <p><strong>Email:</strong> {{ inviteValidation.email }}</p>
          </div>
        </v-alert>

        <!-- Ошибка валидации инвайта -->
        <v-alert
          v-if="inviteInfo && !inviteValidation.valid"
          type="error"
          variant="tonal"
          class="mb-4"
          prominent
        >
          <v-alert-title>Ошибка приглашения</v-alert-title>
          <p>{{ inviteValidation.error }}</p>
          <v-btn
            variant="text"
            color="white"
            class="mt-2"
            @click="router.push('/login')"
          >
            Перейти ко входу
          </v-btn>
        </v-alert>

        <!-- Форма регистрации -->
        <v-card class="elevation-12">
          <v-toolbar color="primary" dark flat>
            <v-toolbar-title>
              {{ inviteInfo ? 'Завершите регистрацию' : 'Регистрация в WikiBase' }}
            </v-toolbar-title>
          </v-toolbar>
          
          <v-card-text>
            <!-- Предупреждение если нет инвайта -->
            <v-alert
              v-if="!inviteInfo"
              type="warning"
              variant="tonal"
              class="mb-4"
            >
              <v-alert-title>Регистрация по приглашениям</v-alert-title>
              <p>Регистрация доступна только по приглашениям. Обратитесь к администратору.</p>
            </v-alert>

            <v-form @submit.prevent="register" :disabled="!inviteValidation.valid">
              <v-text-field
                v-model="form.username"
                label="Имя пользователя"
                name="username"
                prepend-icon="mdi-account"
                type="text"
                required
                :disabled="loading || !inviteValidation.valid"
              />

              <v-text-field
                v-model="form.email"
                label="Email"
                name="email"
                prepend-icon="mdi-email"
                type="email"
                required
                :disabled="loading || !inviteValidation.valid || !!inviteInfo"
                :readonly="!!inviteInfo"
                :hint="inviteInfo ? 'Email из приглашения' : ''"
                persistent-hint
              />

              <v-text-field
                v-model="form.password"
                label="Пароль"
                name="password"
                prepend-icon="mdi-lock"
                type="password"
                required
                :disabled="loading || !inviteValidation.valid"
              />

              <v-text-field
                v-model="form.confirm_password"
                label="Подтвердите пароль"
                name="confirm_password"
                prepend-icon="mdi-lock-check"
                type="password"
                required
                :disabled="loading || !inviteValidation.valid"
              />
            </v-form>
          </v-card-text>
          
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="primary"
              @click="register"
              :loading="loading"
              :disabled="!inviteValidation.valid"
            >
              Зарегистрироваться
            </v-btn>
          </v-card-actions>
          
          <v-card-text class="text-center">
            <router-link to="/login">Уже есть аккаунт? Войти</router-link>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn color="white" text @click="snackbar = false">
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
// @ts-nocheck
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useApiStore } from '@/stores/api'
import { useTenantStore } from '@/stores/tenant'

const router = useRouter()
const route = useRoute()
const api = useApiStore()
const tenantStore = useTenantStore()

const form = ref({
  username: '',
  email: '',
  password: '',
  confirm_password: ''
})

const loading = ref(false)
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

// Инвайт
const inviteInfo = ref<string | null>(null)
const inviteValidation = ref<any>({
  valid: false,
  email: null,
  tenant_name: null,
  role: null,
  error: null
})

/**
 * Валидация инвайта при загрузке
 */
const validateInvite = async (token: string) => {
  try {
    const result = await api.validateInvitation(token)
    inviteValidation.value = result

    if (result.valid) {
      // Автозаполняем email из инвайта
      form.value.email = result.email
    }
  } catch (error: any) {
    console.error('Error validating invite:', error)
    inviteValidation.value = {
      valid: false,
      error: 'Не удалось проверить приглашение'
    }
  }
}

/**
 * Регистрация
 */
const register = async () => {
  // Валидация
  if (!form.value.username || !form.value.email || !form.value.password) {
    showSnackbar('Заполните все поля', 'error')
    return
  }

  if (form.value.password !== form.value.confirm_password) {
    showSnackbar('Пароли не совпадают', 'error')
    return
  }

  if (form.value.password.length < 6) {
    showSnackbar('Пароль должен содержать минимум 6 символов', 'error')
    return
  }

  // Проверка инвайта
  if (!inviteInfo.value) {
    showSnackbar('Регистрация доступна только по приглашениям', 'error')
    return
  }

  if (!inviteValidation.value.valid) {
    showSnackbar('Приглашение недействительно', 'error')
    return
  }

  loading.value = true

  try {
    const response = await api.register({
      username: form.value.username,
      email: form.value.email,
      password: form.value.password,
      invite_token: inviteInfo.value
    })

    // Сохраняем токен
    if (response.access_token) {
      localStorage.setItem('token', response.access_token)
      
      // Инициализируем tenant store после успешной регистрации
      await tenantStore.initStore()
      
      showSnackbar('Регистрация успешна! Добро пожаловать!', 'success')
      
      // Переходим на главную
      setTimeout(() => {
        router.push('/')
      }, 1000)
    } else {
      showSnackbar('Регистрация успешна! Теперь войдите в систему.', 'success')
      router.push('/login')
    }
  } catch (error: any) {
    console.error('Register error:', error)
    const message = error.response?.data?.detail || 'Ошибка регистрации'
    showSnackbar(message, 'error')
  } finally {
    loading.value = false
  }
}

/**
 * Отображение роли на русском
 */
const getRoleDisplay = (role: string): string => {
  const roles: Record<string, string> = {
    'platform_admin': 'Супер-администратор',
    'tenant_admin': 'Администратор',
    'sales_manager': 'Менеджер по продажам',
    'user': 'Пользователь'
  }
  return roles[role] || role
}

const showSnackbar = (text: string, color: string) => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

// Lifecycle
onMounted(async () => {
  // Проверяем наличие invite token в URL
  const inviteToken = route.query.invite as string
  
  if (inviteToken) {
    inviteInfo.value = inviteToken
    await validateInvite(inviteToken)
  } else {
    // Нет инвайта - показываем предупреждение
    inviteValidation.value = {
      valid: false,
      error: 'Регистрация доступна только по приглашениям'
    }
  }
})
</script>
